import React, { useState } from 'react';
import styled from 'styled-components';
import { Button } from '../../components/Button';
import { Modal } from '../../components/Modal';
import {
  FormGroup,
  FormLabel,
  FormInput,
  FormSelect,
  FormRow
} from '../../components/FormField';
import { PosSummary } from './types';

interface NewPosModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: NewPosFormData) => void;
}

export interface NewPosFormData {
  name: string;
  address: string;
  city: string;
  country: string;
  network: 'on' | 'off';
  status: 'prospect' | 'towin' | 'active';
}

export const NewPosModal: React.FC<NewPosModalProps> = ({
  open,
  onClose,
  onSubmit
}) => {
  const [form, setForm] = useState<NewPosFormData>({
    name: '',
    address: '',
    city: '',
    country: '',
    network: 'on',
    status: 'prospect'
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = () => {
    if (!form.name.trim()) return;
    onSubmit(form);
    setForm({
      name: '',
      address: '',
      city: '',
      country: '',
      network: 'on',
      status: 'prospect'
    });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a new point of sale"
      subtitle="Capture a POS you've discovered — we'll match wines against your catalog."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            icon={
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
            }
          >
            Add POS
          </Button>
        </>
      }
    >
      <FormGroup>
        <FormLabel htmlFor="newPOSName" required>
          POS name
        </FormLabel>
        <FormInput
          id="newPOSName"
          name="name"
          value={form.name}
          onChange={handleChange}
          placeholder="e.g., Maaemo"
          required
        />
      </FormGroup>
      <FormGroup>
        <FormLabel htmlFor="newPOSAddress">Address</FormLabel>
        <FormInput
          id="newPOSAddress"
          name="address"
          value={form.address}
          onChange={handleChange}
          placeholder="Street and number"
        />
      </FormGroup>
      <FormRow>
        <FormGroup>
          <FormLabel htmlFor="newPOSCity">City</FormLabel>
          <FormInput
            id="newPOSCity"
            name="city"
            value={form.city}
            onChange={handleChange}
            placeholder="e.g., Oslo"
          />
        </FormGroup>
        <FormGroup>
          <FormLabel htmlFor="newPOSCountry">Country</FormLabel>
          <FormInput
            id="newPOSCountry"
            name="country"
            value={form.country}
            onChange={handleChange}
            placeholder="e.g., Norway"
          />
        </FormGroup>
      </FormRow>
      <FormRow>
        <FormGroup>
          <FormLabel htmlFor="newPOSNetwork">Distribution</FormLabel>
          <FormSelect
            id="newPOSNetwork"
            name="network"
            value={form.network}
            onChange={handleChange}
          >
            <option value="on">On Trade</option>
            <option value="off">Off Trade</option>
          </FormSelect>
        </FormGroup>
        <FormGroup>
          <FormLabel htmlFor="newPOSStatus">Initial status</FormLabel>
          <FormSelect
            id="newPOSStatus"
            name="status"
            value={form.status}
            onChange={handleChange}
          >
            <option value="prospect">Prospect</option>
            <option value="towin">To win over</option>
            <option value="active">Active</option>
          </FormSelect>
        </FormGroup>
      </FormRow>
    </Modal>
  );
};

// ─── Upload List Modal ─────────────────────────────────────────────

interface UploadListModalProps {
  open: boolean;
  onClose: () => void;
}

export const UploadListModal: React.FC<UploadListModalProps> = ({
  open,
  onClose
}) => {
  const [file, setFile] = useState<File | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Upload a list of POS"
      subtitle="Bulk-import points of sale from a spreadsheet. We'll auto-match and flag duplicates."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!file}
            icon={
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            }
          >
            Import
          </Button>
        </>
      }
    >
      <FormGroup>
        <FormLabel>POS list file</FormLabel>
        <DropZone>
          <DropIcon
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </DropIcon>
          <DropText>
            <strong>Drag and drop your file</strong>
            <span>
              or{' '}
              <BrowseBtn as="label" htmlFor="uploadFile">
                browse files
              </BrowseBtn>
            </span>
          </DropText>
          <DropHint>
            XLSX, CSV — required columns: Name, Address, City, Country
          </DropHint>
          <input
            type="file"
            id="uploadFile"
            hidden
            accept=".xlsx,.csv"
            onChange={handleFileChange}
          />
          {file && <FileChip>{file.name}</FileChip>}
        </DropZone>
      </FormGroup>
    </Modal>
  );
};

// ─── Write Email Modal ─────────────────────────────────────────────

interface WriteEmailModalProps {
  open: boolean;
  onClose: () => void;
  selectedPos: PosSummary[];
  onSend: (data: EmailFormData) => void;
}

export interface EmailFormData {
  subject: string;
  message: string;
}

export const WriteEmailModal: React.FC<WriteEmailModalProps> = ({
  open,
  onClose,
  selectedPos,
  onSend
}) => {
  const [form, setForm] = useState<EmailFormData>({
    subject: '',
    message: ''
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = () => {
    if (!form.subject.trim() || !form.message.trim()) return;
    onSend(form);
    setForm({ subject: '', message: '' });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Write an email"
      subtitle={`Send to ${selectedPos.length} selected point${
        selectedPos.length > 1 ? 's' : ''
      } of sale.`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!form.subject.trim() || !form.message.trim()}
            icon={
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            }
          >
            Send email
          </Button>
        </>
      }
    >
      <FormGroup>
        <FormLabel>Recipients</FormLabel>
        <RecipientList>
          {selectedPos.map((pos) => (
            <RecipientChip key={pos.id}>
              {pos.name}
              <RecipientCity>{pos.city}</RecipientCity>
            </RecipientChip>
          ))}
        </RecipientList>
      </FormGroup>
      <FormGroup>
        <FormLabel htmlFor="emailSubject" required>
          Subject
        </FormLabel>
        <FormInput
          id="emailSubject"
          name="subject"
          value={form.subject}
          onChange={handleChange}
          placeholder="e.g., New spring allocation available"
        />
      </FormGroup>
      <FormGroup>
        <FormLabel htmlFor="emailMessage" required>
          Message
        </FormLabel>
        <Textarea
          id="emailMessage"
          name="message"
          value={form.message}
          onChange={handleChange}
          placeholder="Write your message…"
          rows={6}
        />
      </FormGroup>
    </Modal>
  );
};

// ─── Save as Action List Modal ─────────────────────────────────────

interface SaveActionListModalProps {
  open: boolean;
  onClose: () => void;
  selectedPos: PosSummary[];
  onSave: (data: ActionListFormData) => void;
}

export interface ActionListFormData {
  name: string;
  action: string;
  sentTo: string;
  sentToType: 'distributor' | 'sales-team';
  privacy: 'private' | 'shared';
  notes: string;
}

export const SaveActionListModal: React.FC<SaveActionListModalProps> = ({
  open,
  onClose,
  selectedPos,
  onSave
}) => {
  const [form, setForm] = useState<ActionListFormData>({
    name: '',
    action: '',
    sentTo: '',
    sentToType: 'distributor',
    privacy: 'private',
    notes: ''
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = () => {
    if (!form.name.trim()) return;
    onSave(form);
    setForm({
      name: '',
      action: '',
      sentTo: '',
      sentToType: 'distributor',
      privacy: 'private',
      notes: ''
    });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Save as Action List"
      subtitle={`Create an action list with ${selectedPos.length} selected POS.`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!form.name.trim()}
            icon={
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                <polyline points="17 21 17 13 7 13 7 21" />
                <polyline points="7 3 7 8 15 8" />
              </svg>
            }
          >
            Save list
          </Button>
        </>
      }
    >
      <FormGroup>
        <FormLabel htmlFor="alName" required>
          List name
        </FormLabel>
        <FormInput
          id="alName"
          name="name"
          value={form.name}
          onChange={handleChange}
          placeholder="e.g., Oslo lost-POS recovery — Q1 2026"
        />
      </FormGroup>
      <FormGroup>
        <FormLabel htmlFor="alAction">Action</FormLabel>
        <FormInput
          id="alAction"
          name="action"
          value={form.action}
          onChange={handleChange}
          placeholder="e.g., Re-engagement campaign"
        />
      </FormGroup>
      <FormRow>
        <FormGroup>
          <FormLabel htmlFor="alSentTo">Send to</FormLabel>
          <FormInput
            id="alSentTo"
            name="sentTo"
            value={form.sentTo}
            onChange={handleChange}
            placeholder="e.g., Vinforum AS"
          />
        </FormGroup>
        <FormGroup>
          <FormLabel htmlFor="alSentToType">Recipient type</FormLabel>
          <FormSelect
            id="alSentToType"
            name="sentToType"
            value={form.sentToType}
            onChange={handleChange}
          >
            <option value="distributor">Distributor</option>
            <option value="sales-team">Sales team</option>
          </FormSelect>
        </FormGroup>
      </FormRow>
      <FormGroup>
        <FormLabel htmlFor="alPrivacy">Visibility</FormLabel>
        <FormSelect
          id="alPrivacy"
          name="privacy"
          value={form.privacy}
          onChange={handleChange}
        >
          <option value="private">Private</option>
          <option value="shared">Shared with team</option>
        </FormSelect>
      </FormGroup>
      <FormGroup>
        <FormLabel htmlFor="alNotes">Notes</FormLabel>
        <Textarea
          id="alNotes"
          name="notes"
          value={form.notes}
          onChange={handleChange}
          placeholder="Add context about this action list…"
          rows={3}
        />
      </FormGroup>
      <FormGroup>
        <FormLabel>Included POS ({selectedPos.length})</FormLabel>
        <RecipientList>
          {selectedPos.map((pos) => (
            <RecipientChip key={pos.id}>
              {pos.name}
              <RecipientCity>{pos.city}</RecipientCity>
            </RecipientChip>
          ))}
        </RecipientList>
      </FormGroup>
    </Modal>
  );
};

// ─── Shared styled components ──────────────────────────────────────

const RecipientList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px;
  border: 1px solid var(--line);
  border-radius: 7px;
  background: var(--paper);
  max-height: 120px;
  overflow-y: auto;
`;

const RecipientChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-pill, 20px);
  font-size: 12px;
  font-weight: 500;
  color: var(--ink);
`;

const RecipientCity = styled.span`
  color: var(--ink-faint);
  font-weight: 400;
`;

const Textarea = styled.textarea`
  padding: 9px 12px;
  border: 1px solid var(--line);
  border-radius: 7px;
  font-size: 14px;
  font-family: var(--font-sans);
  background: var(--white);
  color: var(--ink);
  resize: vertical;
  min-height: 60px;
  transition: border-color 0.15s;

  &:focus {
    outline: none;
    border-color: var(--terracotta);
  }

  &::placeholder {
    color: var(--ink-faint);
  }
`;

const DropZone = styled.div`
  border: 2px dashed var(--line);
  border-radius: 10px;
  padding: 28px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
  transition: border-color 0.15s;

  &:hover {
    border-color: var(--terracotta);
  }
`;

const DropIcon = styled.svg`
  width: 28px;
  height: 28px;
  color: var(--ink-faint);
`;

const DropText = styled.div`
  font-size: 14px;
  color: var(--ink-muted);

  strong {
    color: var(--ink);
  }
`;

const BrowseBtn = styled.button`
  background: none;
  border: none;
  color: var(--terracotta);
  cursor: pointer;
  font-size: inherit;
  text-decoration: underline;
`;

const DropHint = styled.div`
  font-size: 11.5px;
  color: var(--ink-faint);
`;

const FileChip = styled.div`
  margin-top: 8px;
  padding: 6px 12px;
  background: var(--paper);
  border-radius: var(--radius-pill);
  font-size: 12px;
  color: var(--ink);
`;
